/**
 * Утилиты для работы с датами
 */

/**
 * Безопасное парсинг даты из строки
 * @param dateStr - строка даты в различных форматах
 * @returns Date объект или null если дата невалидна
 */
export function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  
  // If already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  
  // If it's ISO format or other format
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Форматирование диапазона дат для проектов (с годом)
 * @param start - начальная дата
 * @param end - конечная дата
 * @returns отформатированная строка "01.10.2025 - 31.12.2025"
 */
export function formatProjectDateRange(start: string, end: string): string {
  if (!start || !end) return '';
  
  try {
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    
    if (!startDate || !endDate) {
      console.warn('Invalid project dates:', { start, end });
      return 'Неверные даты';
    }
    
    const options: Intl.DateTimeFormatOptions = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    };
    return `${startDate.toLocaleDateString('ru-RU', options)} - ${endDate.toLocaleDateString('ru-RU', options)}`;
  } catch (error) {
    console.error('Error formatting project date range:', { start, end, error });
    return 'Ошибка дат';
  }
}

/**
 * Форматирование диапазона дат для карточек (без года)
 * @param start - начальная дата
 * @param end - конечная дата
 * @returns отформатированная строка "01.10 - 31.12"
 */
export function formatCardDateRange(start: string, end: string): string {
  if (!start || !end) return '';
  
  try {
    const startDate = parseDate(start);
    const endDate = parseDate(end);
    
    if (!startDate || !endDate) {
      console.warn('Invalid card dates:', { start, end });
      return '';
    }
    
    const options: Intl.DateTimeFormatOptions = { 
      day: '2-digit', 
      month: '2-digit' 
    };
    return `${startDate.toLocaleDateString('ru-RU', options)} - ${endDate.toLocaleDateString('ru-RU', options)}`;
  } catch (error) {
    console.error('Error formatting card date range:', { start, end, error });
    return '';
  }
}

/**
 * Форматирование одной даты
 * @param dateStr - строка даты
 * @param includeYear - включать ли год в формат
 * @returns отформатированная дата
 */
export function formatSingleDate(dateStr: string, includeYear: boolean = true): string {
  if (!dateStr) return '';
  
  try {
    const date = parseDate(dateStr);
    if (!date) {
      console.warn('Invalid single date:', dateStr);
      return 'Неверная дата';
    }
    
    const options: Intl.DateTimeFormatOptions = includeYear 
      ? { day: '2-digit', month: '2-digit', year: 'numeric' }
      : { day: '2-digit', month: '2-digit' };
      
    return date.toLocaleDateString('ru-RU', options);
  } catch (error) {
    console.error('Error formatting single date:', dateStr, error);
    return 'Ошибка даты';
  }
}
